import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Link,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom
} from '@mui/material';
import {
  Settings,
  Close,
  ExpandMore,
  ExpandLess,
  Security,
  Analytics,
  ContactMail,
  Info
} from '@mui/icons-material';
import { useConsent } from '../hooks/useConsent';
import { ConsentData } from './GDPRConsentModal';

interface CookieBannerProps {
  onOpenDetailModal?: () => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onOpenDetailModal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasValidConsent, saveConsent, hasPermission } = useConsent();
  
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [consents, setConsents] = useState<ConsentData>({
    required: true,
    analytics: false,
    marketing: false,
    timestamp: new Date()
  });

  // Banner-Sichtbarkeit steuern
  useEffect(() => {
    if (!hasValidConsent) {
      // Kurze Verzögerung für bessere UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [hasValidConsent]);

  // Aktuelle Einstellungen laden
  useEffect(() => {
    if (hasValidConsent) {
      setConsents({
        required: true, // immer true
        analytics: hasPermission('analytics'),
        marketing: hasPermission('marketing'),
        timestamp: new Date()
      });
    }
  }, [hasValidConsent, hasPermission]);

  const handleAcceptAll = async () => {
    const allConsents = {
      ...consents,
      analytics: true,
      marketing: true,
      timestamp: new Date()
    };
    
    const success = await saveConsent(allConsents);
    if (success) {
      setIsVisible(false);
    }
  };

  const handleAcceptNecessary = async () => {
    const necessaryOnly = {
      ...consents,
      analytics: false,
      marketing: false,
      timestamp: new Date()
    };
    
    const success = await saveConsent(necessaryOnly);
    if (success) {
      setIsVisible(false);
    }
  };

  const handleSaveSelection = async () => {
    const updatedConsents = {
      ...consents,
      timestamp: new Date()
    };
    
    const success = await saveConsent(updatedConsents);
    if (success) {
      setIsVisible(false);
    }
  };

  const handleConsentChange = (type: 'analytics' | 'marketing') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConsents(prev => ({
      ...prev,
      [type]: event.target.checked
    }));
  };

  const handleReject = () => {
    setIsVisible(false);
    // Optional: Explizites Ablehnen loggen
  };

  if (!isVisible) return null;

  return (
    <Fade in={isVisible} timeout={500}>
      <Box
        sx={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          left: isMobile ? 16 : 24,
          zIndex: 9999,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 420,
          width: isMobile ? 'calc(100vw - 32px)' : 400
        }}
      >
        <Zoom in={isVisible} timeout={300} style={{ transitionDelay: '200ms' }}>
          <Paper
            elevation={8}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: `2px solid ${theme.palette.primary.main}20`,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 16px 50px rgba(0,0,0,0.2)'
              }
            }}
          >
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Security color="primary" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="600" color="primary">
                  🍪 Cookie-Einstellungen
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleReject}
                sx={{ 
                  color: 'grey.600',
                  '&:hover': { color: 'grey.800' }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Kurztext */}
            <Typography variant="body2" color="text.secondary" mb={2}>
              Wir verwenden Cookies für die CSV-Analyse und optional für erweiterte 
              Funktionen. Ihre Daten werden verschlüsselt übertragen und nach 7 Tagen gelöscht.
            </Typography>

            {/* Quick Actions - Immer sichtbar */}
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleAcceptAll}
                sx={{ minWidth: 120 }}
              >
                Alle akzeptieren
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleAcceptNecessary}
                sx={{ minWidth: 120 }}
              >
                Nur notwendige
              </Button>
            </Box>

            {/* Erweiterte Einstellungen */}
            <Box>
              <Button
                variant="text"
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ 
                  mb: 1,
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Erweiterte Einstellungen
              </Button>

              <Collapse in={isExpanded}>
                <Box sx={{ pl: 1, pb: 2 }}>
                  {/* Notwendige Cookies */}
                  <Box mb={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={true}
                          disabled={true}
                          size="small"
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Security fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="600">
                              Notwendige Cookies
                            </Typography>
                            <Chip label="Immer aktiv" size="small" color="primary" variant="outlined" />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            CSV-Upload und Grundanalyse
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                  </Box>

                  {/* Analytics */}
                  <Box mb={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={consents.analytics}
                          onChange={handleConsentChange('analytics')}
                          size="small"
                          color="secondary"
                        />
                      }
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Analytics fontSize="small" color="secondary" />
                            <Typography variant="body2" fontWeight="600">
                              Statistik & Analyse
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Erweiterte Optimierungsempfehlungen
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                  </Box>

                  {/* Marketing */}
                  <Box mb={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={consents.marketing}
                          onChange={handleConsentChange('marketing')}
                          size="small"
                          color="secondary"
                        />
                      }
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ContactMail fontSize="small" color="secondary" />
                            <Typography variant="body2" fontWeight="600">
                              Marketing & Kontakt
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Beratung zu Hybrid-Speicherlösungen
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                  </Box>

                  {/* Auswahl speichern Button */}
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleSaveSelection}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Auswahl speichern
                  </Button>
                </Box>
              </Collapse>
            </Box>

            {/* Footer Links */}
            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center"
              sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: `1px solid ${theme.palette.divider}` 
              }}
            >
              <Box display="flex" gap={2}>
                <Link
                  href="/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  color="primary"
                  underline="hover"
                >
                  📄 Datenschutz
                </Link>
                <Link
                  href="/impressum"
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="caption"
                  color="primary"
                  underline="hover"
                >
                  ℹ️ Impressum
                </Link>
              </Box>
              
              {onOpenDetailModal && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Info />}
                  onClick={onOpenDetailModal}
                  sx={{ 
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Details
                </Button>
              )}
            </Box>
          </Paper>
        </Zoom>
      </Box>
    </Fade>
  );
};

export default CookieBanner;